import type { UseQueryOptions } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import ky from "ky";
export type AuthStatuses =
  | "AUTHENTICATED"
  | "UNAUTHENTICATED"
  | "ERROR"
  | "LOGGEDOUT";

const queryKeyBase = "ddc-api-auth";

const staleTime = 0;
export const useValidateJwt = (
  baseUrl = "/landing/",
  optionsOverrides?: Partial<UseQueryOptions<AuthStatuses>>
) => {
  const queryKey = [queryKeyBase, baseUrl];
  const previousAuthState = useRef<AuthStatuses | undefined>(undefined);

  const path = `${baseUrl}nxlambda/auth/`;
  const options: UseQueryOptions<AuthStatuses> = {
    refetchInterval: 300000, // 5 mins
    staleTime,
    retry: false,
    ...optionsOverrides,
    queryKey,
    queryFn: () => fetchAuthGet(path)
  };

  const { data, isLoading, isLoadingError } = useQuery(options);

  // Compare current data with previous auth state
  const authStatus = getAuthStatus(
    data,
    isLoadingError,
    previousAuthState.current
  );

  // Update previous auth state after comparison
  useEffect(() => {
    if (data !== undefined) {
      previousAuthState.current = data;
    }
  }, [data]);

  return {
    authStatus,
    isAuthLoading: isLoading
  };
};

const fetchAuthGet = async (path: string): Promise<AuthStatuses> => {
  const response = await ky.get(path, {
    throwHttpErrors: false
  });
  if (response.status === 200) return "AUTHENTICATED";
  if (response.status >= 400 && response.status < 500) return "UNAUTHENTICATED";
  return "ERROR";
};

function getAuthStatus(
  data: AuthStatuses | undefined,
  isLoadingError: boolean,
  previousAuthState: AuthStatuses | undefined
) {
  if (previousAuthState === "AUTHENTICATED" && data === "UNAUTHENTICATED")
    return "LOGGEDOUT";
  if (data) return data;
  if (isLoadingError) return "ERROR";
  return "UNAUTHENTICATED";
}
